import {BeginnerModeLevel} from '@colonist/game-beginner-mode'
import {DiceSettingEnum, GameSpeedSettings} from '@colonist/types'
import {isValueNumericEnum} from '@colonist/validation'
import {BrowserStorage} from './BrowserStorage'
import {I18n} from '@colonist/i18n'
import {MatchmakingCardCategory} from './ui_html/home_page/UIModeSelectorController'
import {MatchType} from '@colonist/game-base-types'
import {UIMSceneEnum} from '../../../shared/enums/UIMSceneEnums'
import {LocalizedPricingProductsPayload, LocalizedPricingSubscriptionsPayload} from '@colonist/mobile'

const storage = new BrowserStorage()

export module StorageWrapper {
    const lastVersionNo = 'versionno'
    const authToken = 'authtoken'
    const careersNotificationBadgeVisible = 'lobbyfootercareersbadgevisible'
    const antiAlias = 'antialias'
    const masterVolume = 'mastervolume'
    const resolution = 'resolution'
    const powerPreference = 'powerpreference'
    const animationStatus = 'animationstatus'
    const logRocket = 'logrocket'
    const keyboardShortcuts = 'keyboardshortcuts'
    const lastMembershipIncentivePopupTime = 'lastmembershipincentivepopuptime'
    const lastShareActivityLinkPopupTime = 'lastshareactivitylinkpopuptime'
    const lastPushReferrersToInviteMoreFriendsPopupTime = 'lastpushreferrerstoinvitemorefriendspopuptime'
    const roomDiceSetting = 'roomdicesetting'
    const roomGameSpeedSetting = 'roomgamespeedsetting'
    const roomFriendlyRobberActiveSetting = 'roomfriendlyrobberactivesetting'
    const deviceStatusBarHeight = 'device-statusBarHeight'
    const deviceSafeAreaInsetBottom = 'device-safeAreaInsetBottom'
    const showPortraitGamePlay = 'showportraitgameplay'
    const firstSessionTimestamp = 'firstsessiontimestamp'
    const deviceBuildNumber = 'mBuildVersion'
    const devicePlatform = 'mPlatformOS'
    const mLoginBypassed = 'mLoginBypassed'
    const mShowAllowNotificationPopup = 'mShowAllowNotificationPopup'
    const mNotificationUserId = 'mNotificationUserId'
    const mPushNotificationRequestStatus = 'mPushNotificationRequestStatus'
    const mLastRequestPushNotificationTime = 'mLastRequestPushNotificationTime'
    const mRequestPushNotificationTimeData = 'mRequestPushNotificationTimeData'
    const mLastShownAllowNotificationPopup = 'mLastShownAllowNotificationPopup'
    const mUserOrientationControllerJourneyStatus = 'mUserOrientationControllerJourneyStatus'
    const mLocalizedStoreProductAndSubscriptionData = 'mLocalizedStoreProductAndSubscriptionData'
    const mLocalizedStoreProductData = 'mLocalizedStoreProductData'
    const mLastScene = 'mLastScene'
    const shouldShowFeedbackForm = 'shouldshowfeedbackform'
    const lastFeedbackFormDate = 'lastfeedbackformdate'
    const showDiscordLinkingSuccessPopup = 'discordlinkingsuccesspopup'
    const discordOnlineUsernamePopupShown = 'discordonlineusernamepopupshown'
    const discordChangeRegionIncentivePopupShown = 'discordchangeregionincentivepopupshown'
    const discordKeepPlayingOnWebPopupShown = 'discordkeepplayingonwebpopupshown'
    const beginnerModeLevel = 'beginnerModeLevel'
    const giftMetadataId = 'giftMetadataId'
    const showPortraitFeedbackPopup = 'showportraitfeedbackpopup'
    const isLandscapeDeprecated = 'isLandscapeDeprecated'
    const lastTimestampDeprecationPopupShown = 'lastTimestampDeprecationPopupShown'
    const userState = 'userState'
    const language = 'language' // used in service worker, don't change
    const lastSelectedMatchmakingMode = 'lastselectedmatchmakingmode'

    export enum PowerPreference {
        Low = 'low-power',
        Default = 'default',
        High = 'high-performance',
    }

    // ====================================================================================================================================
    // LogRocket
    // ====================================================================================================================================

    export function setLogRocket(active: boolean) {
        storage.set(logRocket, active)
    }

    export function getLogRocket(): boolean | null {
        const value = storage.get(logRocket)
        return value == null ? null : value == 'true'
    }

    // ====================================================================================================================================
    // Auth Token
    // ====================================================================================================================================

    export function setAuthToken(token: string) {
        storage.set(authToken, token)
    }

    export function getAuthToken(): string | null {
        return storage.get(authToken)
    }

    // ====================================================================================================================================
    // Canvas Settings
    // ====================================================================================================================================

    export function setKeyboardShortcuts(active: boolean) {
        storage.set(keyboardShortcuts, active)
    }

    export function getKeyboardShortcuts(): boolean | null {
        const value = storage.get(keyboardShortcuts)
        return value == null ? null : value == 'true'
    }

    export function setAntialias(active: boolean) {
        storage.set(antiAlias, active)
    }

    export function getAntialias(): boolean | null {
        const value = storage.get(antiAlias)
        return value == null ? null : value == 'true'
    }

    export function setResolution(no: number) {
        storage.set(resolution, no)
    }

    export function getResolution(): number | null {
        const value = storage.get(resolution)
        return (value == null || isNaN(Number(value))) ? null : Number(value)
    }

    export function setPowerPreference(pref: PowerPreference) {
        storage.set(powerPreference, pref)
    }

    export function getPowerPreference(): StorageWrapper.PowerPreference {
        switch(storage.get(powerPreference, StorageWrapper.PowerPreference.Low)) {
            case StorageWrapper.PowerPreference.Low:
                return StorageWrapper.PowerPreference.Low
            case StorageWrapper.PowerPreference.High:
                return StorageWrapper.PowerPreference.High
            case StorageWrapper.PowerPreference.Default:
                return StorageWrapper.PowerPreference.Default
            default:
                return StorageWrapper.PowerPreference.Low
        }
    }

    export function setAnimation(active: boolean) {
        storage.set(animationStatus, active)
    }

    export function getAnimation(): boolean | null {
        const value = storage.get(animationStatus)
        return value == null ? null : value == 'true'
    }

    // ====================================================================================================================================
    // Version No
    // ====================================================================================================================================

    export function setVersionNo(no: number) {
        storage.set(lastVersionNo, no)
        return true
    }

    export function getVersionNo(): number | null {
        const versionNo = storage.get(lastVersionNo)
        return versionNo == null ? null : Number(versionNo)
    }

    // ====================================================================================================================================
    // Sound
    // ====================================================================================================================================

    export function setMasterVolume(newVolume: number) {
        storage.set(masterVolume, newVolume)
    }

    export function getMasterVolume(): number | undefined {
        const volumeVal = storage.get(masterVolume)
        if(volumeVal == null) return undefined
        let volumeNumber = parseFloat(volumeVal)
        if(isNaN(volumeNumber)) return undefined
        if(volumeNumber < 0.0) volumeNumber = 0.0
        if(volumeNumber > 1.0) volumeNumber = 1.0
        if(!isFinite(volumeNumber)) return undefined
        return volumeNumber
    }

    // ====================================================================================================================================
    // Membership Incentive Popup Timer
    // ====================================================================================================================================

    export function setLastMembershipIncentivePopupTime(membershipIncentivePopupShownTime: Date) {
        storage.set(lastMembershipIncentivePopupTime, membershipIncentivePopupShownTime)
    }

    export function getLastMembershipIncentivePopupTime(): Date | null {
        const lastMembershipIncentivePopupTimeString = storage.get(lastMembershipIncentivePopupTime)
        return !lastMembershipIncentivePopupTimeString ? null : new Date(lastMembershipIncentivePopupTimeString)
    }

    // ====================================================================================================================================
    // Room Game Settings
    // ====================================================================================================================================

    export function setRoomDiceSetting(diceSetting: DiceSettingEnum) {
        storage.set(roomDiceSetting, diceSetting)
    }

    export function getRoomDiceSetting(): DiceSettingEnum | null {
        const diceSetting = storage.get(roomDiceSetting)
        if(diceSetting == null) return null

        const parsedDiceSetting = Number(diceSetting)
        const settingKey = DiceSettingEnum[parsedDiceSetting] as keyof typeof DiceSettingEnum | null
        if(settingKey == null) return null
        const setting = DiceSettingEnum[settingKey]
        return setting
    }

    export function setRoomGameSpeedSetting(gameSpeedSetting: GameSpeedSettings) {
        storage.set(roomGameSpeedSetting, gameSpeedSetting)
    }

    export function getRoomGameSpeedSetting(): GameSpeedSettings | null {
        const gameSpeedSetting = storage.get(roomGameSpeedSetting)
        if(gameSpeedSetting == null) return null

        const parsedGameSpeedSetting = Number(gameSpeedSetting)
        const settingKey = GameSpeedSettings[parsedGameSpeedSetting] as keyof typeof GameSpeedSettings | null
        if(settingKey == null) return null
        const setting = GameSpeedSettings[settingKey]
        return setting
    }

    export function setRoomFriendlyRobberActiveSetting(friendlyRobberActive: boolean) {
        storage.set(roomFriendlyRobberActiveSetting, friendlyRobberActive)
    }

    export function getRoomFriendlyRobberActiveSetting(): boolean | null {
        const friendlyRobberActive = storage.get(roomFriendlyRobberActiveSetting)
        return friendlyRobberActive == null ? null : friendlyRobberActive == 'true'
    }

    // ====================================================================================================================================
    // Mobile Device Information
    // ====================================================================================================================================

    export function clearDeprecationStorage() {
        storage.delete(showPortraitFeedbackPopup)
        storage.delete(isLandscapeDeprecated)
        storage.delete(lastTimestampDeprecationPopupShown)
        storage.delete(mLastRequestPushNotificationTime)
    }

    export function shouldResetGameOrientation(): boolean {
        const portraitGamePlayEnabled = storage.get(showPortraitGamePlay)
        if(portraitGamePlayEnabled == null) return true
        return false
    }

    export function isPortraitGamePlayEnabled(): boolean {
        const portraitGamePlayEnabled = storage.get(showPortraitGamePlay)
        return portraitGamePlayEnabled != 'false'
    }

    export function setPortraitGamePlayEnabled(enabled: boolean) {
        storage.set(showPortraitGamePlay, enabled)
    }

    export function getStatusBarHeight(): number | null {
        const statusBarHeight = storage.get(deviceStatusBarHeight)
        return statusBarHeight == null ? null : Number(statusBarHeight)
    }

    export function setStatusBarHeight(value: number): void {
        storage.set(deviceStatusBarHeight, value)
    }

    export function getSafeAreaInsetBottom(): number | null {
        const safeAreaInsetBottom = storage.get(deviceSafeAreaInsetBottom)
        return safeAreaInsetBottom == null ? null : Number(safeAreaInsetBottom)
    }

    export function setSafeAreaInsetBottom(value: number): void {
        storage.set(deviceSafeAreaInsetBottom, value)
    }

    export function getBuildNumber(): number | null {
        const buildNumber = storage.get(deviceBuildNumber)
        return buildNumber == null ? null : Number(buildNumber)
    }

    export function getPlatform(): string | null {
        const platform = storage.get(devicePlatform)
        return platform
    }

    export function getIfUserBypassedLogin(): boolean {
        const bypassed = storage.get(mLoginBypassed)
        return bypassed == 'true'
    }

    export function setUserBypassedLogin(value: boolean) {
        storage.set(mLoginBypassed, value)
    }

    export function setLastShownAllowNotificationPopupTime(date: Date | string) {
        storage.set(mLastShownAllowNotificationPopup, date instanceof Date ? date.toISOString() : date)
    }

    export function setNotificationUserId(userId: string) {
        storage.set(mNotificationUserId, userId)
    }

    export function getNotificationUserId(): string | null {
        return storage.get(mNotificationUserId)
    }

    export function getLastShownAllowNotificationPopupTime(): Date | null {
        const timestamp = storage.get(mLastShownAllowNotificationPopup)
        return !timestamp ? null : new Date(timestamp)
    }

    export function setShouldShowAllowNotificationPopup(show: boolean) {
        storage.set(mShowAllowNotificationPopup, show)
    }

    export function getShouldShowAllowNotificationPopup(): boolean | null {
        const show = storage.get(mShowAllowNotificationPopup)
        return show == null ? null : show == 'true'
    }

    export function setPushNotificationRequestStatus(status: boolean) {
        storage.set(mPushNotificationRequestStatus, status)
    }

    export function getPushNotificationRequestStatus(): boolean | null {
        return storage.get(mPushNotificationRequestStatus) == 'true'
    }

    export function getLastRequestPushNotificationTime(): Date | null {
        const timestamp = storage.get(mLastRequestPushNotificationTime)
        return !timestamp ? null : new Date(timestamp)
    }

    export function setRequestPushNotificationTimeData(data: Record<string, Date>) {
        storage.set(mRequestPushNotificationTimeData, JSON.stringify(data))
    }

    export function getRequestPushNotificationTimeData(): Record<string, Date> {
        return JSON.parse(storage.get(mRequestPushNotificationTimeData) ?? '{}')
    }

    export function setUserOrientationControllerJourneyStatus(status: string) {
        storage.set(mUserOrientationControllerJourneyStatus, status)
    }

    export function getUserOrientationControllerJourneyStatus(): string | null {
        return storage.get(mUserOrientationControllerJourneyStatus)
    }

    export function setFirstSessionTimestamp(timestamp: number) {
        storage.set(firstSessionTimestamp, timestamp)
    }

    export function getFirstSessionTimestamp(): number | null {
        return storage.get(firstSessionTimestamp) == null ? null : Number(storage.get(firstSessionTimestamp))
    }

    export function setLocalizedStoreSubscriptionData(data: LocalizedPricingSubscriptionsPayload[]) {
        storage.set(mLocalizedStoreProductAndSubscriptionData, JSON.stringify(data))
    }

    export function setLocalizedStoreProductData(data: LocalizedPricingProductsPayload[]) {
        storage.set(mLocalizedStoreProductData, JSON.stringify(data))
    }

    export function getLocalizedStoreSubscriptionData(): LocalizedPricingSubscriptionsPayload[] {
        try {
            return JSON.parse(storage.get(mLocalizedStoreProductAndSubscriptionData) ?? '[]') as LocalizedPricingSubscriptionsPayload[]
        } catch{
            return []
        }
    }

    export function getLocalizedStoreProductData(): LocalizedPricingProductsPayload[] {
        try {
            return JSON.parse(storage.get(mLocalizedStoreProductData) ?? '[]') as LocalizedPricingProductsPayload[]
        } catch{
            return []
        }
    }

    export function setLastScene(scene: UIMSceneEnum) {
        storage.set(mLastScene, scene)
    }

    export function getLastScene(): UIMSceneEnum | null {
        const scene = storage.get(mLastScene)
        if(scene == null) return null

        const sceneEnum = Number(scene)
        if(isNaN(sceneEnum)) return null
        if(!Object.values(UIMSceneEnum).includes(sceneEnum)) return null

        return sceneEnum as UIMSceneEnum
    }

    export function clearLastScene() {
        storage.delete(mLastScene)
    }

    // ====================================================================================================================================
    // NPS Form
    // ====================================================================================================================================

    export function removeShouldShowFeedbackForm() {
        storage.delete(shouldShowFeedbackForm)
    }

    export function setShouldShowFeedbackForm(show: boolean) {
        storage.set(shouldShowFeedbackForm, show)
    }

    export function getShouldShowFeedbackForm(): boolean | null {
        const show = storage.get(shouldShowFeedbackForm)
        return show == null ? null : show == 'true'
    }

    export function setLastFeedbackFormDate(date: Date | string) {
        storage.set(lastFeedbackFormDate, date instanceof Date ? date.toISOString() : date)
    }

    export function getLastFeedbackFormDate(): Date | null {
        const lastFeedbackFormDateString = storage.get(lastFeedbackFormDate)
        return !lastFeedbackFormDateString ? null : new Date(lastFeedbackFormDateString)
    }

    // ====================================================================================================================================
    // Beginner Mode
    // ====================================================================================================================================

    export function incrementBeginnerModeLevel() {
        const currentLevel = getBeginnerModeLevel()
        if(currentLevel == null) return
        const nextLevel = currentLevel + 1
        if(!isValueNumericEnum(nextLevel, BeginnerModeLevel)) return
        storage.set(beginnerModeLevel, nextLevel)
    }

    export function decrementBeginnerModeLevel() {
        const currentLevel = getBeginnerModeLevel()
        if(currentLevel == null) return
        const nextLevel = currentLevel - 1
        if(!isValueNumericEnum(nextLevel, BeginnerModeLevel)) return
        storage.set(beginnerModeLevel, nextLevel)
    }

    export function resetBeginnerModeLevel() {
        storage.set(beginnerModeLevel, BeginnerModeLevel.Level1)
    }

    export function getBeginnerModeLevel(): BeginnerModeLevel {
        const defaultLevel = BeginnerModeLevel.Level1
        const level = storage.get(beginnerModeLevel)
        if(level == null) {
            storage.set(beginnerModeLevel, defaultLevel)
            return defaultLevel
        }

        return Number(level)
    }

    // ====================================================================================================================================
    // Discord Account Linked
    // ====================================================================================================================================

    export function setDiscordAccountLinked() {
        storage.set(showDiscordLinkingSuccessPopup, true)
    }

    export function getDiscordAccountLinked(): boolean {
        return storage.get(showDiscordLinkingSuccessPopup) == 'true'
    }

    export function removeDiscordAccountLinked() {
        storage.delete(showDiscordLinkingSuccessPopup)
    }

    // ====================================================================================================================================
    // Discord Online Username Popup
    // ====================================================================================================================================

    export function setDiscordOnlineUsernamePopupShown() {
        storage.set(discordOnlineUsernamePopupShown, true)
    }

    export function getDiscordOnlineUsernamePopupShown(): boolean {
        return storage.get(discordOnlineUsernamePopupShown) == 'true'
    }

    // ====================================================================================================================================
    // Discord Change Region Incentive Popup
    // ====================================================================================================================================

    export function setDiscordChangeRegionIncentivePopupShown() {
        storage.set(discordChangeRegionIncentivePopupShown, true)
    }

    export function getDiscordChangeRegionIncentivePopupShown(): boolean {
        return storage.get(discordChangeRegionIncentivePopupShown) == 'true'
    }

    // ====================================================================================================================================
    // Discord Keep Playing On Web Popup
    // ====================================================================================================================================

    export function setDiscordKeepPlayingOnWebPopupShown() {
        storage.set(discordKeepPlayingOnWebPopupShown, true)
    }

    export function getDiscordKeepPlayingOnWebPopupShown(): boolean {
        return storage.get(discordKeepPlayingOnWebPopupShown) == 'true'
    }

    // ====================================================================================================================================
    // UserState
    // ====================================================================================================================================

    export function setUserState(state: Record<any, any>) {
        storage.set(userState, JSON.stringify(state))
    }

    export function clearUserState() {
        setUserState({})
    }

    export function getUserState(): Record<string, unknown> | undefined {
        const stored = storage.get(userState)
        if(stored == null) return undefined
        try {
            return JSON.parse(stored)
        } catch{
            return undefined
        }
    }

    export function getLanguage(): string {
        return storage.get(language) ?? I18n.defaultLanguage
    }

    export function setLanguage(value: string) {
        storage.set(language, value)
        void updateServiceWorkerLanguage(value)
    }

    // ====================================================================================================================================
    // Gifting
    // ====================================================================================================================================

    export function setGiftMetadataId(id: string) {
        storage.set(giftMetadataId, id)
    }

    export function getGiftMetadataId(): string | null {
        return storage.get(giftMetadataId)
    }

    export function clearGiftMetadataId() {
        storage.delete(giftMetadataId)
    }

    // ====================================================================================================================================
    // Share Activity Link Popup Timer
    // ====================================================================================================================================

    export function setLastShareActivityLinkPopupTime(shareActivityLinkPopupShownTime: Date) {
        storage.set(lastShareActivityLinkPopupTime, shareActivityLinkPopupShownTime)
    }

    export function getLastShareActivityLinkPopupTime(): Date | null {
        const lastShareActivityLinkPopupTimeString = storage.get(lastShareActivityLinkPopupTime)
        return !lastShareActivityLinkPopupTimeString ? null : new Date(lastShareActivityLinkPopupTimeString)
    }

    // ====================================================================================================================================
    // Push Referrers To Invite More Friends Popup Timer
    // ====================================================================================================================================

    export function setLastPushReferrersToInviteMoreFriendsPopupTime(pushReferrersToInviteMoreFriendsPopupTime: Date) {
        storage.set(lastPushReferrersToInviteMoreFriendsPopupTime, pushReferrersToInviteMoreFriendsPopupTime)
    }

    export function getLastPushReferrersToInviteMoreFriendsPopupTime(): Date | null {
        const lastPushReferrersToInviteMoreFriendsPopupTimeString = storage.get(lastPushReferrersToInviteMoreFriendsPopupTime)
        return !lastPushReferrersToInviteMoreFriendsPopupTimeString ? null : new Date(lastPushReferrersToInviteMoreFriendsPopupTimeString)
    }

    interface LastSelectedMatchmakingData {
        category: MatchmakingCardCategory
        matchType: MatchType
    }

    export function setLastSelectedMatchmakingMode(matchmakingData: LastSelectedMatchmakingData) {
        storage.set(lastSelectedMatchmakingMode, JSON.stringify({
            category: matchmakingData.category,
            matchType: matchmakingData.matchType,
        }))
    }

    export function getLastSelectedMatchmakingMode(): LastSelectedMatchmakingData | null {
        const stored = storage.get(lastSelectedMatchmakingMode)
        if(stored == null) return null
        try {
            return JSON.parse(stored) as LastSelectedMatchmakingData
        } catch{
            return null
        }
    }
}

async function updateServiceWorkerLanguage(language: string) {
    if('serviceWorker' in navigator) {
        await navigator.serviceWorker.ready
        navigator.serviceWorker.controller?.postMessage({type: 'languageChange', language: language})
    }
}
